import type { SurveyFragmentFragment } from '@aurora/shared-generated/types/graphql-types';
import getConfig from 'next/config';

export function getTruncatedQuestionId(questionId: string): string {
  return questionId.split('.').at(-1);
}

export const surveyQuestionAnswerDelimiter = '.';

export function removeDisabledQuestionAndAnswers(
  surveyDisabledInputs: string[],
  survey: SurveyFragmentFragment
) {
  const nestedQuestionsToRemove = [];

  const disabledQuestions = survey?.questions.filter(question =>
    surveyDisabledInputs.includes(getTruncatedQuestionId(question.id))
  );

  disabledQuestions.forEach(question => {
    question.branches.forEach(branch => {
      if (branch.trigger !== 'default_flow') {
        nestedQuestionsToRemove.push(branch.target);
      }
    });
  });

  const questions = survey?.questions
    .filter(question => {
      const questionId = getTruncatedQuestionId(question.id);
      return (
        !surveyDisabledInputs.includes(questionId) && !nestedQuestionsToRemove.includes(questionId)
      );
    })
    .map(question => ({
      ...question,
      id: getTruncatedQuestionId(question.id),
      answers: question.answers.filter(
        answer =>
          !surveyDisabledInputs.includes(
            `${getTruncatedQuestionId(question.id)}${surveyQuestionAnswerDelimiter}${answer.id}`
          )
      )
    }));

  return {
    ...survey,
    questions
  };
}

export function getQuestionIdWithStepNumber(
  survey: SurveyFragmentFragment
): { step: number; id: string }[] {
  const surveyWithStepNumber = [];

  const nonDefaultFlowQuestions: { parentId: string; id: string }[] = [];

  survey.questions.forEach(question => {
    question.branches.forEach(branch => {
      if (branch.trigger !== 'default_flow') {
        nonDefaultFlowQuestions.push({
          parentId: getTruncatedQuestionId(question.id),
          id: branch.target
        });
      }
    });
  });

  let count = 0;
  const questionWithBranchToCountMap = {};

  survey.questions.forEach(question => {
    const questionId = getTruncatedQuestionId(question.id);
    const nonDefaultFlowQuestion = nonDefaultFlowQuestions.find(({ id }) => id === questionId);
    if (!nonDefaultFlowQuestion) {
      surveyWithStepNumber.push({
        step: (count += 1),
        id: questionId
      });
    } else {
      let localCount = questionWithBranchToCountMap[nonDefaultFlowQuestion.parentId];
      if (localCount == null) {
        count += 1;
        questionWithBranchToCountMap[nonDefaultFlowQuestion.parentId] = count;
        localCount = questionWithBranchToCountMap[nonDefaultFlowQuestion.parentId];
      }

      surveyWithStepNumber.push({
        step: localCount,
        id: questionId
      });
    }
  });

  return surveyWithStepNumber;
}

export function getSurveyQuestionCount(survey: SurveyFragmentFragment) {
  const nonDefaultFlowQuestions: string[] = [];
  let finalCount = 0;

  survey.questions.forEach(question => {
    const localNonDefaultFlowQuestions = [];
    question.branches.forEach(branch => {
      if (branch.trigger !== 'default_flow') {
        localNonDefaultFlowQuestions.push(branch.target);
      }
    });
    if (localNonDefaultFlowQuestions.length > 0) {
      nonDefaultFlowQuestions.push(...localNonDefaultFlowQuestions);
      finalCount += 1;
    }
  });

  const questionsWithoutBranches = survey.questions.filter(question => {
    const questionId = getTruncatedQuestionId(question.id);
    return !nonDefaultFlowQuestions.includes(questionId);
  });

  return questionsWithoutBranches.length + finalCount;
}

export function isSurveysConfigEnabled() {
  return getConfig()?.publicRuntimeConfig?.surveysEnabled;
}
